import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import moment from "moment";
import { getSessionData } from "utils/common";

/** Calendar YYYY-MM-DD — never send Date objects (UTC shift breaks From/To). */
function toHistoryFilterYmd(value, fallbackMoment) {
  const fallback = fallbackMoment || moment();
  if (value == null || value === "") {
    return fallback.format("YYYY-MM-DD");
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
    const parsed = moment(
      trimmed,
      ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "DD.MM.YYYY", moment.ISO_8601],
      true
    );
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("YYYY-MM-DD");
  }
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : fallback.format("YYYY-MM-DD");
}

const initialState = {
  activeTab: "deals", // deals | orders | closed  (spec: 3 sub-tabs)
  historyList: undefined,
  historyLoading: false,
  historyError: null,
  historySearchText: "",
  currentPageHistory: 1,
  totalRecordHistory: 0,
  /** Orders tab chip: all | pending | done */
  ordersStateFilter: "all",
  /** From/To stored as YYYY-MM-DD strings so Find always sends the picked calendar days. */
  historyFilterValue: {
    startDate: moment().subtract(3, "months").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    segment: null,
    client: null,
    symbol: null,
  },
  isHistoryGet: false,
};

function buildHistoryPayload(data) {
  const { userId, roles } = getSessionData();
  const {
    searchText,
    pageNo,
    pageSize,
    client,
    segment,
    symbol,
    endDate,
    startDate,
  } = data;

  let startYmd = toHistoryFilterYmd(startDate, moment().subtract(3, "months"));
  let endYmd = toHistoryFilterYmd(endDate, moment());
  if (moment(endYmd).isBefore(moment(startYmd), "day")) {
    const swap = startYmd;
    startYmd = endYmd;
    endYmd = swap;
  }

  return {
    userId: data.userId ? data.userId : userId,
    pageNo: pageNo || 1,
    pageSize: pageSize || 200,
    segmentId: segment && Object.keys(segment).length > 0 ? segment.value : null,
    clientId:
      client && Object.keys(client).length > 0
        ? client.value
        : roles?.[0] === "User"
          ? userId
          : null,
    symbol: symbol && Object.keys(symbol).length > 0 ? symbol.value : null,
    searchText: searchText || "",
    startDate: startYmd,
    endDate: endYmd,
  };
}

function createHistoryThunk(type, endpoint, includeDates = true) {
  return createAsyncThunk(type, (data) => {
    return new Promise((resolve, reject) => {
      const payload = buildHistoryPayload(data);
      if (!includeDates) {
        delete payload.startDate;
        delete payload.endDate;
      }
      axios
        .post(endpoint, payload)
        .then(({ data: res }) => {
          if (res.status == 200) {
            resolve({ data: res.data, appendData: !!data.appendData });
          } else {
            reject(res);
          }
        })
        .catch((error) => reject(error));
    });
  });
}

export const getDealsList = createHistoryThunk("history/get-deals", "deals/get", true);
export const getOrderHistoryList = createHistoryThunk(
  "history/get-order-history",
  "orders/get-history",
  true
);
export const getPendingOrdersList = createHistoryThunk(
  "history/get-pending",
  "orders/get-pending",
  false
);
export const getClosedPositionsList = createHistoryThunk(
  "history/get-closed",
  "positions/get-closed",
  true
);

export const fetchHistoryByTab = createAsyncThunk(
  "history/fetch-by-tab",
  async (data, { dispatch }) => {
    const tab = data.tab || "deals";
    if (tab === "orders") return dispatch(getOrderHistoryList(data)).unwrap();
    if (tab === "closed") return dispatch(getClosedPositionsList(data)).unwrap();
    return dispatch(getDealsList(data)).unwrap();
  }
);

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    setHistoryActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setHistorySearchText: (state, action) => {
      state.historySearchText = action.payload;
    },
    setStartPageHistory: (state, action) => {
      state.currentPageHistory = action.payload;
    },
    setHistoryList: (state, action) => {
      state.historyList = action.payload;
    },
    setHistoryFilterValue: (state, action) => {
      const next = action.payload || {};
      state.historyFilterValue = {
        ...state.historyFilterValue,
        ...next,
        startDate: toHistoryFilterYmd(
          next.startDate,
          moment(state.historyFilterValue.startDate || undefined)
        ),
        endDate: toHistoryFilterYmd(
          next.endDate,
          moment(state.historyFilterValue.endDate || undefined)
        ),
      };
    },
    setTotalRecordHistory: (state, action) => {
      state.totalRecordHistory = action.payload;
    },
    setIsHistoryGet: (state, action) => {
      state.isHistoryGet = action.payload;
    },
    setOrdersStateFilter: (state, action) => {
      state.ordersStateFilter = action.payload || "all";
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => {
      state.historyLoading = true;
      state.historyError = null;
    };
    const rejected = (state, action) => {
      state.historyList = [];
      state.historyLoading = false;
      state.historyError =
        action.error?.message ||
        action.payload?.message ||
        "Failed to load history. Check API / stored procedures.";
    };
    const fulfilled = (state, action) => {
      const records =
        action.payload?.data?.records ||
        action.payload?.data?.data?.records ||
        [];
      if (action.payload.appendData) {
        state.historyList = [...(state.historyList || []), ...records];
      } else {
        state.historyList = records;
      }
      state.totalRecordHistory = action.payload?.data?.totalRecords || 0;
      state.historyLoading = false;
      state.historyError = null;
    };

    [getDealsList, getOrderHistoryList, getPendingOrdersList, getClosedPositionsList].forEach(
      (thunk) => {
        builder.addCase(thunk.pending, pending);
        builder.addCase(thunk.rejected, rejected);
        builder.addCase(thunk.fulfilled, fulfilled);
      }
    );
  },
});

export const {
  setHistoryActiveTab,
  setHistorySearchText,
  setStartPageHistory,
  setHistoryList,
  setHistoryFilterValue,
  setTotalRecordHistory,
  setIsHistoryGet,
  setOrdersStateFilter,
} = historySlice.actions;

export default historySlice.reducer;
